import { useEffect, useState } from 'react';
import GenericErrorModal from './components/GenericErrorModal/GenericErrorModal';
import { useDispatch, useSelector } from 'react-redux';
import { hideErrorModal, clearError } from './store/reducers/ui';
import { errorActions } from './store/reducers/error';
import config from '@/config';
import { Outlet, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  Config as MavatarConfig,
  TokenManager as MavatarTokenManager,
} from '@mavatar/melingo-voice-and-avatar-sdk';
import { authService } from '@/services/auth';
import { CurrentRouteProvider } from './context/CurrentRouteContext';
import UseCheckAuth from './hooks/UseCheckAuth';
import UseSessionHeartbeat from './hooks/UseSessionHeartbeat';
import { useDrawer } from './context/DrawerContext';
import { authSelector, interfaceLanguageSelector } from './store/reducers/auth';
import { RemoveTrailingSlash } from './hooks/RemoveTrailingSlash';
import { useAppSelector } from './store';

import { ToastContainer } from 'react-toastify';
import Drawer from 'react-modern-drawer';
import AppSettings from './components/Settings/Settings';
import MobileDrawer from './components/Settings/MobileDrawer';
import ContactUsDrawer from './components/ContactUsDrawer/ContactUsDrawer';
import WelcomeConfetti from './components/WelcomeConfetti';
import AppDownloadRecommandation from './components/AppDownloadRecommandation/AppDownloadRecommandation';
import AddNewClass from './components/AddNewClass/AddNewClass';
import EditClass from './components/EditClass/EditClass';
import AppLoader from './components/AppLoader';
import ReactGA from 'react-ga4';
import { UserPropertiesGA } from './types/analytics';
import AddNewStudent from './components/AddNewStudent/AddNewStudent';
import TopicsSideDrawer from './pages/dashboard/topics-drawer/TopicsSideDrawer';
import LevelChangeModal from './components/LevelChangeModal/LevelChangeModal';
import UserUtils from '@/utils/user';
import OfflineNotification from './components/OfflineNotification/OfflineNotification';
import NotificationsPopup from '@/components/Notifications/NotificationsPopup/NotificationsPopup';
import TutorTopics from '@/utils/tutorTopics';

const queryClient = new QueryClient();

const App = () => {
  // Initialize app authentication check
  const { user, appLoaded, showWelcomeScreen, loggedIn, showAppRecommandationScreen } =
    useAppSelector(authSelector);
  UseCheckAuth();
  UseSessionHeartbeat();
  const drawer = useDrawer();
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    showErrorModal,
    errorTitle,
    errorMessage,
    i18nKey,
    navigateOnClose,
    errorEyebrow,
    errorButtonText,
    errorIcon,
  } = useSelector((state: any) => state.ui);
  const [studentLevelChanged, setStudentLevelChanged] = useState<string>('');
  const [levelChangeHandled, setLevelChangeHandled] = useState(false);

  const handleErrorModalClose = () => {
    dispatch(hideErrorModal());

    dispatch(errorActions.hideError());
    setTimeout(() => {
      if (navigateOnClose) {
        if (navigateOnClose === 'refresh') {
          window.location.reload();
        } else {
          navigate(navigateOnClose);
        }
      }
      dispatch(clearError());
    }, 500);
  };

  useEffect(() => {
    let isUnlocked: boolean = false;

    const unlockAutoPlayAudioOption = () => {
      isUnlocked = true;
      document.removeEventListener('click', unlockAutoPlayAudioOption);
      TutorTopics.unlockAutoPlayAudioOption();
    };

    document.addEventListener('click', unlockAutoPlayAudioOption);
    return () => {
      if (!isUnlocked) {
        document.removeEventListener('click', unlockAutoPlayAudioOption);
      }
    };
  }, []);

  useEffect(() => {
    if (user && user.progress?.userLevel) {
      let userProperties: UserPropertiesGA = {
        user_organization_name: user.metadata.organizationDetails.organizationId,
        user_native_language: user.metadata.languagePreferences.nativeLanguage,
        user_interface_language: user.metadata.languagePreferences.interfaceLanguage,
        user_level: user?.progress?.userLevel || 0,
        user_group_name: user.metadata.organizationDetails.groupId,
        user_role: user?.metadata?.role,
      };

      switch (user?.metadata?.role) {
        case 'admin':
          userProperties = {
            ...userProperties,
            ell_admin_user: 1,
          };
          break;
        case 'teacher':
          userProperties = {
            ...userProperties,
            ell_teacher_user: 1,
          };
          break;
        case 'student':
          userProperties = {
            ...userProperties,
            ell_student_user: 1,
          };
          break;
        default:
          break;
      }

      ReactGA.gtag('set', 'user_properties', userProperties);
    }

    MavatarConfig.setEnvironment(config.mavatarEnvironments);
    // MavatarConfig.baseStreamApi = 'ws://localhost:5000/stream/';
    MavatarConfig.tokenManager =
      user?.id && user?.metadata?.role && !UserUtils.isAdmin(user)
        ? new MavatarTokenManager({
            createToken: async () => {
              const response = await authService.createVoiceServiceToken(user?.id || '');

              if (
                response &&
                response.data?.status?.toLowerCase() === 'success' &&
                response.data.data
              ) {
                return response.data.data;
              } else {
                throw new Error('Error create mavatar accessToken');
              }
            },
          })
        : new MavatarTokenManager({
            createToken: async () => {
              return '';
            },
          });
    MavatarConfig.tokenManager.createJwt();
  }, [user?.id, user?.metadata?.role]);

  useEffect(() => {
    if (interfaceLanguage) {
      document.body.style.direction = interfaceLanguage.direction;
    }
  }, [interfaceLanguage]);

  useEffect(() => {
    const status = user?.metadata.status;
    if ((status === 'LEVEL_UP' || status === 'LEVEL_DOWN') && !levelChangeHandled) {
      if (localStorage.getItem('skipLevelChangedMessage') !== 'true') {
        setStudentLevelChanged(status);
      }
      setLevelChangeHandled(true);
    }
    if (status === 'LEVEL_UP' || status === 'LEVEL_DOWN') {
      localStorage.removeItem('skipLevelChangedMessage');
    }
  }, [user]);

  const refreshLevelChangeState = () => {
    if (studentLevelChanged !== '') {
      setStudentLevelChanged('');
    }
  };

  let content = <div className='App'>{appLoaded ? <Outlet /> : <AppLoader />}</div>;

  if (loggedIn) {
    if (showWelcomeScreen) {
      content = <WelcomeConfetti />;
    } else if (showAppRecommandationScreen) {
      content = <AppDownloadRecommandation />;
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CurrentRouteProvider>
        <RemoveTrailingSlash />
        {content}
        <ToastContainer />
        <GenericErrorModal
          show={showErrorModal}
          title={errorTitle}
          message={errorMessage}
          i18nKey={i18nKey}
          eyebrow={errorEyebrow}
          buttonText={errorButtonText}
          icon={errorIcon}
          onClose={handleErrorModalClose}
        />
        <LevelChangeModal
          show={studentLevelChanged !== ''}
          status={user?.metadata.status ?? ''}
          onRefresh={refreshLevelChangeState}
        />
        <OfflineNotification />
        <NotificationsPopup />
        {user && (
          <>
            <Drawer
              open={drawer.isDrawerOpen}
              onClose={drawer.closeDrawer}
              direction={interfaceLanguage?.direction === 'rtl' ? 'left' : 'right'}
              className='app-settings app-drawer'
            >
              <AppSettings />
            </Drawer>
            <Drawer
              open={drawer.isTopicsOpen}
              onClose={drawer.closeTopicsDrawer}
              direction={interfaceLanguage?.direction === 'rtl' ? 'left' : 'right'}
              className='app-settings app-drawer'
            >
              <TopicsSideDrawer topicId={drawer.topicId} />
            </Drawer>
            <Drawer
              open={drawer.isMobileDrawerOpen}
              onClose={drawer.closeMobileDrawer}
              direction={interfaceLanguage?.direction === 'rtl' ? 'left' : 'right'}
              className='app-mobile-drawer app-settings app-drawer'
            >
              <MobileDrawer />
            </Drawer>
            <Drawer
              open={drawer.isContactUsDrawerOpen}
              onClose={drawer.closeContactUsDrawer}
              direction={interfaceLanguage?.direction === 'rtl' ? 'left' : 'right'}
              className='app-drawer app-drawer--contact-us'
            >
              <ContactUsDrawer
                isOpen={drawer.isContactUsDrawerOpen}
                onClose={drawer.closeContactUsDrawer}
              />
            </Drawer>

            <Drawer
              open={drawer.isAddClassDrawerOpen}
              onClose={drawer.closeAddClass}
              direction={interfaceLanguage?.direction === 'rtl' ? 'left' : 'right'}
              className='app-drawer'
            >
              <AddNewClass />
            </Drawer>

            <Drawer
              open={drawer.isEditClassDrawerOpen}
              onClose={drawer.closeEditClass}
              direction={interfaceLanguage?.direction === 'rtl' ? 'left' : 'right'}
              className='app-drawer'
            >
              <EditClass />
            </Drawer>
            <Drawer
              open={drawer.isAddNewStudentDrawerOpen}
              onClose={drawer.closeAddStudent}
              direction={'right'}
              className='app-drawer'
            >
              <AddNewStudent groupId={drawer.groupId} isOpen={drawer.isAddNewStudentDrawerOpen} />
            </Drawer>
          </>
        )}
      </CurrentRouteProvider>
    </QueryClientProvider>
  );
};

export default App;
